import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogOut, CreditCard, Settings, Shield, Plus, ExternalLink, Calendar, Search, User as UserIcon, Trash2, Key, Eye, EyeOff } from 'lucide-react';
import logo from '../assets/logo.jpg';
import { authService } from '../services/authService';
import { BoardStorage } from '../utils/storage';
import { Board, CaseMetadata } from '../types';
import toast from 'react-hot-toast';
import NewBoardDialog from './NewBoardDialog';
import { useTheme } from '../hooks/useTheme';
import ProfileEditDialog, { ProfileData } from './ProfileEditDialog';
import { messageService } from '../services/messageService';
import { Send, X, BookOpen } from 'lucide-react';
import GuideDialog from './GuideDialog';
import RecoveryUtility from './RecoveryUtility';

const DashboardPage: React.FC = () => {
  useTheme(); // Initialize theme
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(authService.getCurrentUser());
  const [boards, setBoards] = useState<Board[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isProfileDialogOpen, setIsProfileDialogOpen] = useState(false);
  const [isGuideOpen, setIsGuideOpen] = useState(false);
  const [isMessageAdminOpen, setIsMessageAdminOpen] = useState(false);
  const [messageSubject, setMessageSubject] = useState('');
  const [messageContent, setMessageContent] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [boardToDelete, setBoardToDelete] = useState<Board | null>(null);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({ current: '', new: '', confirm: '' });
  const [showPasswords, setShowPasswords] = useState({ current: false, new: false, confirm: false });
  const [changingPassword, setChangingPassword] = useState(false);
  const [isRecoveryOpen, setIsRecoveryOpen] = useState(false);
  const [hasBackups, setHasBackups] = useState(false);

  useEffect(() => {
    // Check if any backups exist in localStorage
    for (let i = 0; i < localStorage.length; i++) {
      if (localStorage.key(i)?.startsWith('fibre_backup_')) {
        setHasBackups(true);
        break;
      }
    }
  }, []);

  // Mock billing status for now
  const billingStatus = {
    plan: 'pro',
    status: 'active',
    nextBillDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()
  };

  useEffect(() => {
    loadBoards();
    // Subscribe to auth changes to reflect profile updates immediately
    const unsubscribe = authService.subscribe(() => {
      setCurrentUser(authService.getCurrentUser());
    });
    return () => unsubscribe();
  }, []);

  const loadBoards = async () => {
    try {
      const userBoards = await BoardStorage.getInstance().getAllBoards(currentUser?.id || null);
      // Sort by lastModified desc
      userBoards.sort((a, b) => b.updatedAt - a.updatedAt);
      setBoards(userBoards);
    } catch (error) {
      console.error('Failed to load boards:', error);
      toast.error('Failed to load investigations');
    } finally {
      setIsLoading(false);
    }
  };

  const handleLogout = () => {
    navigate('/signout-success');
  };

  const handleCreateBoard = () => {
    setIsDialogOpen(true);
  };

  const handleBoardCreateConfirm = async (title: string, metadata: CaseMetadata) => {
    try {
      const newBoard: Board = {
        id: `board_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        title,
        createdAt: Date.now(),
        updatedAt: Date.now(),
        nodes: [],
        edges: [],
        caseMetadata: {
          ...metadata,
          dateOpened: Date.now()
        }
      };

      await BoardStorage.getInstance().saveBoard(newBoard, currentUser?.id || null);

      // Navigate to app (App will load most recent board)
      // Navigate to app with board ID
      navigate(`/app?boardId=${newBoard.id}`);
      toast.success('Investigation created successfully');
    } catch (error) {
      console.error('Failed to create board:', error);
      toast.error('Failed to create investigation');
    }
  };

  const handleOpenBoard = async (boardId: string) => {
    navigate(`/app?boardId=${boardId}`);
  };

  const handleProfileSave = async (profile: ProfileData) => {
    const savePromise = authService.updateUserProfile(profile);
    await toast.promise(savePromise, {
      loading: 'Saving profile...',
      success: 'Profile updated successfully',
      error: 'Failed to update profile'
    });
  };

  const handleMessageAdmin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!messageSubject || !messageContent) return;

    setSendingMessage(true);
    try {
      await messageService.sendMessageToAdmins(messageSubject, messageContent);
      toast.success('Message sent to Admin Team');
      setIsMessageAdminOpen(false);
      setMessageSubject('');
      setMessageContent('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const handleDeleteBoard = async () => {
    if (!boardToDelete) return;
    try {
      await BoardStorage.getInstance().deleteBoard(boardToDelete.id, currentUser?.id || null);
      setBoards(prev => prev.filter(b => b.id !== boardToDelete.id));
      toast.success('Investigation deleted');
    } catch (error) {
      console.error('Failed to delete board:', error);
      toast.error('Failed to delete investigation');
    } finally {
      setBoardToDelete(null);
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (passwordData.new.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (passwordData.new !== passwordData.confirm) {
      toast.error('Passwords do not match');
      return;
    }
    setChangingPassword(true);
    try {
      await authService.changePassword(passwordData.current, passwordData.new);
      toast.success('Password changed successfully');
      setIsPasswordDialogOpen(false);
      setPasswordData({ current: '', new: '', confirm: '' });
    } catch (error: any) {
      if (error.code === 'auth/wrong-password' || error.code === 'auth/invalid-credential') {
        toast.error('Current password is incorrect');
      } else {
        toast.error('Failed to change password');
      }
    } finally {
      setChangingPassword(false);
    }
  };

  return (
    <div className="bg-gray-50 dark:bg-gray-900 transition-colors duration-200 overflow-y-auto min-h-full">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/app" className="flex items-center gap-3 hover:opacity-80 transition-opacity">
              <img src={logo} alt="Fibre" className="h-6 w-auto" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">Fibre</h1>
            </Link>

            <div className="flex items-center gap-3">
              {currentUser?.role === 'admin' && (
                <button
                  onClick={() => navigate('/admin')}
                  className="
                    hidden sm:inline-flex items-center gap-2 px-3 py-2 
                    bg-purple-600 text-white rounded-md hover:bg-purple-700 transition
                    shadow-lg shadow-purple-500/20 font-bold
                  "
                >
                  <Shield size={16} />
                  Admin Panel
                </button>
              )}
              <button
                onClick={() => setIsGuideOpen(true)}
                className="
                  hidden sm:inline-flex items-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600
                  text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 
                  bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700
                  transition-colors duration-200
                "
              >
                <BookOpen size={16} />
                Guide
              </button>

              {!currentUser?.has2FAEnabled && (
                <Link
                  to="/2fa/setup"
                  className="
                    hidden sm:inline-flex items-center gap-2 px-3 py-2 border border-transparent text-sm 
                    font-medium rounded-md text-white bg-green-600 hover:bg-green-700
                    transition-colors duration-200
                  "
                >
                  <Shield size={16} />
                  Enable 2FA
                </Link>
              )}

              <button
                onClick={handleLogout}
                className="
                  inline-flex items-center gap-2 px-3 py-2 border border-transparent text-sm 
                  font-medium rounded-md text-gray-700 bg-gray-100 hover:bg-gray-200 
                  dark:text-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600
                  transition-colors duration-200
                "
              >
                <LogOut size={16} />
                Sign out
              </button>

              <button
                onClick={() => setIsRecoveryOpen(true)}
                className="
                  inline-flex items-center gap-2 px-4 py-2 border-2 border-amber-400 dark:border-amber-500 text-sm 
                  font-bold rounded-md text-amber-700 bg-amber-100 hover:bg-amber-200 
                  dark:text-amber-100 dark:bg-amber-600 dark:hover:bg-amber-500
                  transition-all duration-200 shadow-md transform hover:scale-105 active:scale-95
                "
              >
                <Shield size={18} className="animate-pulse" />
                RESCUE CENTER
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:py-8 lg:px-8 space-y-8">

        {/* Welcome Section */}
        <div className="flex flex-col sm:flex-row justify-between items-center text-center sm:text-left">
          <div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white">
              Dashboard
            </h2>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Welcome back, {currentUser?.name}. Manage your investigations and account.
            </p>
          </div>
          {/* Mobile Guide Button */}
          <button
            onClick={() => setIsGuideOpen(true)}
            className="mt-4 sm:hidden inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800"
          >
            <BookOpen size={16} /> User Guide
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">

          {/* Main Column: Investigations */}
          <div className="lg:col-span-2 space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-xl font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Search size={20} />
                Open Investigations
              </h3>
              <button
                onClick={handleCreateBoard}
                className="
                  inline-flex items-center gap-2 px-4 py-2 border border-transparent text-sm font-medium rounded-md
                  text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600
                  shadow-sm transition-all duration-200
                "
              >
                <Plus size={16} />
                New Board
              </button>
            </div>

            {/* Board List */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
              {isLoading ? (
                <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                  Loading investigations...
                </div>
              ) : boards.length > 0 ? (
                <ul className="divide-y divide-gray-200 dark:divide-gray-700">
                  {boards.map((board) => (
                    <li key={board.id} className="hover:bg-gray-50 dark:hover:bg-gray-750 transition-colors duration-150">
                      <div className="px-6 py-4 flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 truncate">
                            {board.title}
                          </p>
                          <p className="mt-1 text-[10px] sm:text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Modified: {new Date(board.updatedAt).toLocaleDateString()}
                          </p>
                        </div>
                        <div className="ml-4 flex-shrink-0 flex items-center gap-2">
                          <button
                            onClick={() => handleOpenBoard(board.id)}
                            className="
                              inline-flex items-center gap-1 px-3 py-1.5 border border-gray-300 dark:border-gray-600
                              text-xs font-medium rounded-md text-gray-700 dark:text-gray-300
                              bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600
                              transition-colors
                            "
                          >
                            Open
                            <ExternalLink size={12} />
                          </button>
                          <button
                            onClick={() => setBoardToDelete(board)}
                            className="
                              inline-flex items-center p-1.5 border border-gray-300 dark:border-gray-600
                              rounded-md text-gray-400 hover:text-red-500 hover:border-red-300 dark:hover:border-red-700
                              bg-white dark:bg-gray-700 hover:bg-red-50 dark:hover:bg-red-900/20
                              transition-colors
                            "
                            title="Delete investigation"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="p-12 text-center">
                  <img src={logo} alt="No investigations" className="mx-auto h-12 w-auto mb-4 opacity-50 grayscale" />
                  <p className="text-gray-600 dark:text-gray-400 mb-4">No investigations found.</p>
                  <button
                    onClick={handleCreateBoard}
                    className="text-blue-600 dark:text-blue-400 hover:underline text-sm"
                  >
                    Create your first board
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar: Billing & Profile */}
          <div className="space-y-6">

            {/* Investigator Profile Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <UserIcon size={20} />
                Investigator Profile
              </h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Investigator Name</label>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{currentUser?.name || 'Unknown'}</p>
                </div>
                {/* Placeholder for future Agency/Badge editing */}
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Badge Number</label>
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">{currentUser?.badgeNumber || 'Not set'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Title</label>
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">{currentUser?.title || 'Not set'}</p>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">Agency</label>
                  <p className="text-sm text-gray-500 dark:text-gray-400 italic">{currentUser?.agency || 'Not set'}</p>
                </div>
                <button
                  onClick={() => setIsProfileDialogOpen(true)}
                  className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Edit Profile
                </button>
                <button
                  onClick={() => setIsMessageAdminOpen(true)}
                  className="w-full mt-4 flex items-center justify-center gap-2 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition"
                >
                  <Send size={14} className="text-blue-600" />
                  Message Admin
                </button>
              </div>
            </div>

            {/* Subscription Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <CreditCard size={20} />
                Subscription
              </h3>

              <div className="bg-slate-50 dark:bg-slate-900/50 rounded-md p-4 mb-4 border border-slate-100 dark:border-slate-800">
                <div className="flex justify-between items-start mb-2">
                  <div>
                    <p className="font-medium text-gray-900 dark:text-white">Fibre Pro</p>
                    <p className="text-xs text-green-600 dark:text-green-400 font-medium uppercase tracking-wide">Active</p>
                  </div>
                  <span className="text-lg font-bold text-gray-900 dark:text-white">$4.99<span className="text-xs font-normal text-gray-500">/mo</span></span>
                </div>
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mt-2">
                  <Calendar size={12} />
                  Next billing: {billingStatus.nextBillDate}
                </div>
              </div>

              <div className="space-y-2">
                <Link
                  to="/settings/billing"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Settings size={16} />
                  Manage Subscription
                </Link>
                <Link
                  to="/pricing"
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
                >
                  View Plans
                </Link>
              </div>
            </div>

            {/* Profile/Security Card */}
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Shield size={20} />
                Security
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-750 rounded-md">
                  <div className="flex items-center gap-3">
                    <span className={`h-2.5 w-2.5 rounded-full ${currentUser?.has2FAEnabled ? 'bg-green-500' : 'bg-yellow-500'}`}></span>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Two-Factor Auth</span>
                  </div>
                  <span className="text-xs text-gray-500 dark:text-gray-400">
                    {currentUser?.has2FAEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                </div>

                {!currentUser?.has2FAEnabled && (
                  <Link
                    to="/2fa/setup"
                    className="block w-full text-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-slate-800 hover:bg-slate-900 dark:bg-slate-700 dark:hover:bg-slate-600 transition-colors"
                  >
                    Setup 2FA
                  </Link>
                )}

                {currentUser?.has2FAEnabled && (
                  <button
                    onClick={async () => {
                      try {
                        await authService.disable2FA();
                        toast.success('Two-factor authentication disabled');
                      } catch (error) {
                        toast.error('Failed to disable 2FA');
                      }
                    }}
                    className="block w-full text-center px-4 py-2 border border-red-300 dark:border-red-700 text-sm font-medium rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                  >
                    Disable 2FA
                  </button>
                )}

                {/* Change Password */}
                <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                  <button
                    onClick={() => setIsPasswordDialogOpen(true)}
                    className="w-full flex items-center justify-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <Key size={16} />
                    Change Password
                  </button>
                </div>
              </div>
            </div>

          </div>
        </div>
      </main>

      <NewBoardDialog
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onCreate={handleBoardCreateConfirm}
        investigatorName={currentUser?.name || ''}
      />

      <GuideDialog
        isOpen={isGuideOpen}
        onClose={() => setIsGuideOpen(false)}
      />

      <ProfileEditDialog
        isOpen={isProfileDialogOpen}
        onClose={() => setIsProfileDialogOpen(false)}
        onSave={handleProfileSave}
        currentProfile={{
          name: currentUser?.name || '',
          badgeNumber: currentUser?.badgeNumber,
          title: currentUser?.title,
          agency: currentUser?.agency
        }}
      />

      {/* Message Admin Modal */}
      {isMessageAdminOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Shield size={18} className="text-blue-600" />
                Contact Admin Team
              </h3>
              <button onClick={() => setIsMessageAdminOpen(false)} className="text-gray-400 hover:text-gray-500">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleMessageAdmin} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Subject</label>
                <input
                  type="text"
                  required
                  className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Issue or Request..."
                  value={messageSubject}
                  onChange={e => setMessageSubject(e.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Message</label>
                <textarea
                  required
                  className="w-full px-3 py-2 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white min-h-[100px]"
                  placeholder="How can we help you?"
                  value={messageContent}
                  onChange={e => setMessageContent(e.target.value)}
                />
              </div>
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={() => setIsMessageAdminOpen(false)}
                  className="mr-3 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={sendingMessage}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50 flex items-center gap-2"
                >
                  {sendingMessage ? 'Sending...' : 'Send Secure Message'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Board Confirmation Dialog */}
      {boardToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-sm w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                <Trash2 size={20} className="text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Delete Investigation</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">This action cannot be undone.</p>
              </div>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to permanently delete <strong>"{boardToDelete.title}"</strong> and all its data?
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setBoardToDelete(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteBoard}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Change Password Dialog */}
      {isPasswordDialogOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95 duration-200">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Key size={18} className="text-blue-600" />
                Change Password
              </h3>
              <button onClick={() => { setIsPasswordDialogOpen(false); setPasswordData({ current: '', new: '', confirm: '' }); }} className="text-gray-400 hover:text-gray-500">
                <X size={20} />
              </button>
            </div>

            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Current Password</label>
                <div className="relative">
                  <input
                    type={showPasswords.current ? 'text' : 'password'}
                    required
                    className="w-full px-3 py-2 pr-10 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={passwordData.current}
                    onChange={e => setPasswordData(prev => ({ ...prev, current: e.target.value }))}
                  />
                  <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPasswords(p => ({ ...p, current: !p.current }))}>
                    {showPasswords.current ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">New Password</label>
                <div className="relative">
                  <input
                    type={showPasswords.new ? 'text' : 'password'}
                    required
                    minLength={8}
                    className="w-full px-3 py-2 pr-10 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    placeholder="Min. 8 characters"
                    value={passwordData.new}
                    onChange={e => setPasswordData(prev => ({ ...prev, new: e.target.value }))}
                  />
                  <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPasswords(p => ({ ...p, new: !p.new }))}>
                    {showPasswords.new ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                  </button>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Confirm New Password</label>
                <div className="relative">
                  <input
                    type={showPasswords.confirm ? 'text' : 'password'}
                    required
                    minLength={8}
                    className="w-full px-3 py-2 pr-10 border rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                    value={passwordData.confirm}
                    onChange={e => setPasswordData(prev => ({ ...prev, confirm: e.target.value }))}
                  />
                  <button type="button" className="absolute inset-y-0 right-0 pr-3 flex items-center" onClick={() => setShowPasswords(p => ({ ...p, confirm: !p.confirm }))}>
                    {showPasswords.confirm ? <EyeOff className="h-4 w-4 text-gray-400" /> : <Eye className="h-4 w-4 text-gray-400" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end pt-2 gap-3">
                <button
                  type="button"
                  onClick={() => { setIsPasswordDialogOpen(false); setPasswordData({ current: '', new: '', confirm: '' }); }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={changingPassword}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
                >
                  {changingPassword ? 'Changing...' : 'Change Password'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <RecoveryUtility
        isOpen={isRecoveryOpen}
        onClose={() => setIsRecoveryOpen(false)}
      />
    </div>
  );
};

export default DashboardPage;
